import { createContext, useState, useContext, ReactNode, useCallback } from 'react';
import { ThemeOptions } from '@mui/material';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { paletteDark, paletteLight } from '../colors';
import {
  getButtonOptions,
  getCheckboxOptions,
  getTypographyOptions,
  getProgressBarOptions,
  getFormControlLabelOptions,
  getListOptions,
  getSelectOptions,
  getPaperOptions,
  getModalOptions,
  getChipOptions,
  getTextFieldOptions,
} from '../ui';

export enum ThemeMode {
  DARK = 'dark',
  LIGHT = 'light',
}
declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: false;
    sm: false;
    md: false;
    lg: false;
    xl: false;
    mobile: true;
    desktop: true;
    container: true;
  }
}

const getTheme = (mode: ThemeMode) => {
  const palette = mode === ThemeMode.LIGHT ? paletteLight : paletteDark;

  const baseTheme = createTheme({
    palette: { ...palette, mode },
    spacing: 4,
    breakpoints: {
      values: {
        mobile: 1024,
        container: 532,
      },
    },
  } as unknown as ThemeOptions);
  const { typography, typographyComponents } = getTypographyOptions(baseTheme);
  const { progressBarComponent } = getProgressBarOptions(baseTheme);
  const { buttonComponents } = getButtonOptions(baseTheme);
  const { listComponents } = getListOptions(baseTheme);
  const { selectComponents } = getSelectOptions(baseTheme);
  const { formControlLabelComponents } = getFormControlLabelOptions(baseTheme);
  const { checkboxComponents } = getCheckboxOptions(baseTheme);
  const { paperComponents } = getPaperOptions(baseTheme);
  const { modalComponent } = getModalOptions(baseTheme);
  const { chipComponent } = getChipOptions(baseTheme);
  const { textFieldComponents } = getTextFieldOptions(baseTheme);

  return createTheme(baseTheme, {
    typography,
    components: {
      MuiButton: buttonComponents,
      MuiList: listComponents,
      MuiCheckbox: checkboxComponents,
      MuiTypography: typographyComponents,
      MuiLinearProgress: progressBarComponent,
      MuiFormControlLabel: formControlLabelComponents,
      MuiSelect: selectComponents,
      MuiPaper: paperComponents,
      MuiDialog: modalComponent,
      MuiChip: chipComponent,
      MuiTextField: textFieldComponents,
    },
  });
};

type ThemeContextType = {
  themeMode: ThemeMode;
  isLightTheme: boolean;
  changeTheme: (themeMode: ThemeMode) => void;
};

const ThemeContext = createContext<ThemeContextType>({
  themeMode: ThemeMode.LIGHT,
  isLightTheme: true,
  changeTheme: () => {},
});

type ThemeProviderProps = {
  children: ReactNode;
  themeMode: ThemeMode;
};

export const ThemeProvider = ({ children, themeMode: defaultThemeMode }: ThemeProviderProps) => {
  const [themeMode, setThemeMode] = useState(defaultThemeMode);

  const changeTheme = useCallback((newTheme: ThemeMode) => {
    setThemeMode(newTheme);
    // TODO:: will be removed after testing
    localStorage.setItem('theme', newTheme);
  }, []);

  return (
    <ThemeContext.Provider
      value={{
        themeMode: themeMode,
        changeTheme,
        isLightTheme: themeMode === ThemeMode.LIGHT,
      }}
    >
      <MuiThemeProvider theme={getTheme(themeMode)}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => useContext(ThemeContext);
